import type { SessionUser } from "@wakaskul/shared";
import { create } from "zustand";

/**
 * The access token lives in memory only — never localStorage.
 *
 * A token in localStorage is readable by any script that gets onto the page,
 * and this application will eventually render user-supplied text. Losing the
 * token on refresh is fine: the refresh token sits in an httpOnly cookie the
 * page cannot read, and `/auth/refresh` silently restores the session on boot.
 */
interface AuthState {
  accessToken: string | null;
  user: SessionUser | null;
  /** True until the initial refresh attempt settles, so routes don't flash. */
  initialising: boolean;

  setSession: (token: string, user: SessionUser) => void;
  setUser: (user: SessionUser) => void;
  clear: () => void;
  setInitialised: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  initialising: true,

  setSession: (accessToken, user) => set({ accessToken, user, initialising: false }),
  setUser: (user) => set({ user }),
  clear: () => set({ accessToken: null, user: null, initialising: false }),
  setInitialised: () => set({ initialising: false }),
}));

/** Read outside React, for the fetch wrapper. */
export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;
export const getCurrentUser = (): SessionUser | null => useAuthStore.getState().user;
